import React, { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../../lib/supabase';
import './SportsDaysModule.css';

const SportsDaysModule = ({ currentUser, onBack, onTabChange }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [sportsEvents, setSportsEvents] = useState([]);
  const [studentRegistrations, setStudentRegistrations] = useState([]);
  const [sportsRaces, setSportsRaces] = useState([]);
  const [sportsResults, setSportsResults] = useState([]);
  const [sportsAnnouncements, setSportsAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('🔍 SportsDaysModule mounted with currentUser:', currentUser);
    fetchSportsData();
  }, []);

  const fetchSportsData = async () => {
    try {
      console.log('🔄 Starting to fetch sports data...');
      setLoading(true);
      setError(null);

      // Try with regular supabase client first
      console.log('🔍 Testing Supabase connection with regular client...');
      let testResponse = await supabase.from('sports_events').select('count');
      console.log('✅ Regular client test result:', testResponse);

      if (testResponse.error) {
        console.log('⚠️ Regular client failed, trying admin client...');
        testResponse = await supabaseAdmin.from('sports_events').select('count');
        console.log('✅ Admin client test result:', testResponse);
      }

      // Fetch all sports data separately (more reliable than complex joins)
      console.log('📥 Fetching sports events...');
      let eventsResponse = await supabase.from('sports_events').select('*').order('event_date', { ascending: true });
      if (eventsResponse.error) {
        console.log('⚠️ Regular client failed for events, trying admin client...');
        eventsResponse = await supabaseAdmin.from('sports_events').select('*').order('event_date', { ascending: true });
      }
      console.log('📥 Events response:', eventsResponse);

      console.log('📥 Fetching registrations...');
      let registrationsResponse = await supabase.from('sports_event_registrations').select('*').order('registration_date', { ascending: false });
      if (registrationsResponse.error) {
        console.log('⚠️ Regular client failed for registrations, trying admin client...');
        registrationsResponse = await supabaseAdmin.from('sports_event_registrations').select('*').order('registration_date', { ascending: false });
      }
      console.log('📥 Registrations response:', registrationsResponse);

      console.log('📥 Fetching students...');
      let studentsResponse = await supabase.from('saas_students').select('student_id, name, class_display, class_no');
      if (studentsResponse.error) {
        console.log('⚠️ Regular client failed for students, trying admin client...');
        studentsResponse = await supabaseAdmin.from('saas_students').select('student_id, name, class_display, class_no');
      }
      console.log('📥 Students response:', studentsResponse);

      console.log('📥 Fetching races...');
      let racesResponse = await supabase.from('sports_races').select('*').order('scheduled_time', { ascending: true });
      if (racesResponse.error) {
        console.log('⚠️ Regular client failed for races, trying admin client...');
        racesResponse = await supabaseAdmin.from('sports_races').select('*').order('scheduled_time', { ascending: true });
      }
      console.log('📥 Races response:', racesResponse);

      console.log('📥 Fetching results...');
      let resultsResponse = await supabase.from('sports_results').select('*').order('created_at', { ascending: false });
      if (resultsResponse.error) {
        console.log('⚠️ Regular client failed for results, trying admin client...');
        resultsResponse = await supabaseAdmin.from('sports_results').select('*').order('created_at', { ascending: false });
      }
      console.log('📥 Results response:', resultsResponse);

      console.log('📥 Fetching announcements...');
      let announcementsResponse = await supabase.from('sports_announcements').select('*').eq('is_active', true).order('published_at', { ascending: false });
      if (announcementsResponse.error) {
        console.log('⚠️ Regular client failed for announcements, trying admin client...');
        announcementsResponse = await supabaseAdmin.from('sports_announcements').select('*').eq('is_active', true).order('published_at', { ascending: false });
      }
      console.log('📥 Announcements response:', announcementsResponse);

      // Handle responses
      if (eventsResponse.error) {
        console.error('❌ Events error:', eventsResponse.error);
        throw eventsResponse.error;
      }
      if (registrationsResponse.error) {
        console.error('❌ Registrations error:', registrationsResponse.error);
        throw registrationsResponse.error;
      }
      if (studentsResponse.error) {
        console.error('❌ Students error:', studentsResponse.error);
        throw studentsResponse.error;
      }
      if (racesResponse.error) {
        console.error('❌ Races error:', racesResponse.error);
        throw racesResponse.error;
      }
      if (resultsResponse.error) {
        console.error('❌ Results error:', resultsResponse.error);
        throw resultsResponse.error;
      }
      if (announcementsResponse.error) {
        console.error('❌ Announcements error:', announcementsResponse.error);
        throw announcementsResponse.error;
      }

      console.log('✅ All data fetched successfully');
      console.log('📊 Sample events data:', eventsResponse.data?.[0]);
      console.log('📊 Sample registrations data:', registrationsResponse.data?.[0]);
      console.log('📊 Sample students data:', studentsResponse.data?.[0]);
      
      setSportsEvents(eventsResponse.data || []);
      setStudentRegistrations(registrationsResponse.data || []);
      setSportsRaces(racesResponse.data || []);
      setSportsResults(resultsResponse.data || []);
      setSportsAnnouncements(announcementsResponse.data || []);

      // Store students data for later use in display
      window.sportsStudentsData = studentsResponse.data || [];

    } catch (error) {
      console.error('❌ Error fetching sports data:', error);
      setError('載入體育日資料時發生錯誤，請稍後再試。');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions to get combined data
  const getStudentName = (studentId) => {
    if (!window.sportsStudentsData) return studentId;
    const student = window.sportsStudentsData.find(s => s.student_id === studentId);
    return student ? student.name : studentId;
  };

  const getStudentClass = (studentId) => {
    if (!window.sportsStudentsData) return '未知';
    const student = window.sportsStudentsData.find(s => s.student_id === studentId);
    return student ? student.class_display : '未知';
  };

  const getEventName = (eventId) => {
    if (!sportsEvents.length) return eventId;
    const event = sportsEvents.find(e => e.id === eventId);
    return event ? event.event_name : eventId;
  };

  const handleViewChange = (view) => {
    setActiveView(view);
  };

  const renderDashboard = () => (
    <div className="sports-dashboard">
      <h2>🏃‍♂️ 體育日模組</h2>
      <p>歡迎來到體育日管理系統！這裡您可以管理學校的體育活動、比賽和運動會。</p>
      
      <div className="dashboard-stats">
        <div className="stat-card">
          <h3>進行中活動</h3>
          <p className="stat-number">{sportsEvents.filter(e => e.event_status === 'ongoing').length}</p>
        </div>
        <div className="stat-card">
          <h3>已安排活動</h3>
          <p className="stat-number">{sportsEvents.filter(e => e.event_status === 'scheduled').length}</p>
        </div>
        <div className="stat-card">
          <h3>總參與人數</h3>
          <p className="stat-number">{sportsEvents.reduce((sum, e) => sum + (e.current_participants || 0), 0)}</p>
        </div>
        <div className="stat-card">
          <h3>已報名學生</h3>
          <p className="stat-number">{studentRegistrations.length}</p>
        </div>
        <div className="stat-card">
          <h3>比賽項目</h3>
          <p className="stat-number">{sportsRaces.length}</p>
        </div>
        <div className="stat-card">
          <h3>最新公告</h3>
          <p className="stat-number">{sportsAnnouncements.length}</p>
        </div>
      </div>

      <div className="upcoming-events">
        <h3>即將舉行的活動</h3>
        <div className="events-grid">
          {sportsEvents.slice(0, 3).map(event => (
            <div key={event.id} className="event-card">
              <div className="event-header">
                <h4>{event.event_name}</h4>
                <span className={`event-status ${event.event_status}`}>
                  {event.event_status === 'scheduled' ? '已安排' : 
                   event.event_status === 'ongoing' ? '進行中' : 
                   event.event_status === 'completed' ? '已完成' : '已取消'}
                </span>
              </div>
              <div className="event-details">
                <p><strong>日期:</strong> {event.event_date}</p>
                <p><strong>時間:</strong> {event.start_time} - {event.end_time}</p>
                <p><strong>場地:</strong> {event.venue_id ? '主田径场' : '待定'}</p>
                <p><strong>參與人數:</strong> {event.current_participants || 0}/{event.max_participants || 0}</p>
                {event.description && <p><strong>描述:</strong> {event.description}</p>}
              </div>
              <div className="event-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => handleViewChange('event-details')}
                >
                  查看詳情
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleViewChange('registration')}
                >
                  報名管理
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {sportsAnnouncements.length > 0 && (
        <div className="recent-announcements">
          <h3>最新公告</h3>
          <div className="announcements-preview">
            {sportsAnnouncements.slice(0, 3).map(announcement => (
              <div key={announcement.id} className="announcement-preview-card">
                <div className="announcement-preview-header">
                  <h4>{announcement.title}</h4>
                  <span className={`priority-badge ${announcement.priority}`}>
                    {announcement.priority === 'urgent' ? '緊急' : 
                     announcement.priority === 'high' ? '高' : 
                     announcement.priority === 'normal' ? '一般' : '低'}
                  </span>
                </div>
                <p className="announcement-preview-content">
                  {announcement.content.length > 100 
                    ? `${announcement.content.substring(0, 100)}...` 
                    : announcement.content}
                </p>
                <div className="announcement-preview-footer">
                  <span className="announcement-type">
                    {announcement.announcement_type === 'race_call' ? '🏃‍♀️ 比賽召集' :
                     announcement.announcement_type === 'schedule_update' ? '📅 賽程更新' :
                     announcement.announcement_type === 'result_announcement' ? '🏆 結果公布' :
                     announcement.announcement_type === 'emergency_alert' ? '⚠️ 緊急通知' : '📢 一般公告'}
                  </span>
                  <span className="announcement-time">
                    {new Date(announcement.published_at).toLocaleString('zh-TW')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderEvents = () => (
    <div className="sports-events">
      <div className="view-header">
        <button className="btn btn-back" onClick={() => handleViewChange('dashboard')}>
          ← 返回儀表板
        </button>
        <h2>體育活動管理</h2>
        <button className="btn btn-primary">+ 新增活動</button>
      </div>
      
      <div className="events-table">
        <table>
          <thead>
            <tr>
              <th>活動代碼</th>
              <th>活動名稱</th>
              <th>日期</th>
              <th>時間</th>
              <th>場地</th>
              <th>狀態</th>
              <th>參與人數</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {sportsEvents.map(event => (
              <tr key={event.id}>
                <td>{event.event_code}</td>
                <td>
                  <div>
                    <div>{event.event_name}</div>
                    {event.event_name_en && <div className="event-name-en">{event.event_name_en}</div>}
                  </div>
                </td>
                <td>{event.event_date}</td>
                <td>{event.start_time} - {event.end_time}</td>
                <td>{event.venue_id ? '主田径场' : '待定'}</td>
                <td>
                  <span className={`status-badge ${event.event_status}`}>
                    {event.event_status === 'scheduled' ? '已安排' : 
                     event.event_status === 'ongoing' ? '進行中' : 
                     event.event_status === 'completed' ? '已完成' : '已取消'}
                  </span>
                </td>
                <td>{event.current_participants || 0}/{event.max_participants || 0}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn btn-small btn-primary">編輯</button>
                    <button className="btn btn-small btn-secondary">查看</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderRegistration = () => (
    <div className="sports-registration">
      <div className="view-header">
        <button className="btn btn-back" onClick={() => handleViewChange('dashboard')}>
          ← 返回儀表板
        </button>
        <h2>報名管理</h2>
        <button className="btn btn-primary">+ 新增報名</button>
      </div>
      
      <div className="registration-table">
        <table>
          <thead>
            <tr>
              <th>學生編號</th>
              <th>學生姓名</th>
              <th>班級</th>
              <th>比賽項目</th>
              <th>組別</th>
              <th>種子時間/成績</th>
              <th>最佳成績</th>
              <th>狀態</th>
              <th>備註</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {studentRegistrations.map(registration => (
              <tr key={registration.id}>
                <td>{registration.student_id}</td>
                <td>{getStudentName(registration.student_id)}</td>
                <td>{getStudentClass(registration.student_id)}</td>
                <td>{getEventName(registration.event_id)}</td>
                <td>{registration.category}</td>
                <td>
                  {registration.seed_time ? `${registration.seed_time}s` : 
                   registration.seed_distance ? `${registration.seed_distance}m` : 
                   registration.seed_height ? `${registration.seed_height}m` : '-'}
                </td>
                <td>
                  {registration.previous_best ? 
                    (registration.seed_time ? `${registration.previous_best}s` : 
                     registration.seed_distance ? `${registration.previous_best}m` : 
                     registration.seed_height ? `${registration.previous_best}m` : registration.previous_best) : '-'}
                </td>
                <td>
                  <span className={`status-badge ${registration.status}`}>
                    {registration.status === 'confirmed' ? '已確認' : 
                     registration.status === 'pending' ? '待確認' : 
                     registration.status === 'cancelled' ? '已取消' : registration.status}
                  </span>
                </td>
                <td>{registration.notes}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn btn-small btn-primary">編輯</button>
                    <button className="btn btn-small btn-secondary">查看</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="sports-results">
      <div className="view-header">
        <button className="btn btn-back" onClick={() => handleViewChange('dashboard')}>
          ← 返回儀表板
        </button>
        <h2>比賽結果</h2>
        <button className="btn btn-primary">+ 錄入結果</button>
      </div>
      
      <div className="results-content">
        <div className="results-summary">
          <h3>🏆 最新比賽結果</h3>
          <div className="results-grid">
            {sportsResults.slice(0, 6).map(result => (
              <div key={result.id} className="result-card">
                <h4>{result.race_id}</h4>
                <div className="result-list">
                  <div className="result-item">
                    <span className="position">
                      {result.finish_position === 1 ? '🥇' : 
                       result.finish_position === 2 ? '🥈' : 
                       result.finish_position === 3 ? '🥉' : `${result.finish_position}th`}
                    </span>
                    <span className="student-name">
                      {getStudentName(result.student_id)} ({getStudentClass(result.student_id)})
                    </span>
                    <span className="result-time">
                      {result.finish_time ? `${result.finish_time}s` : 
                       result.finish_distance ? `${result.finish_distance}m` : 
                       result.finish_height ? `${result.finish_height}m` : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="results-actions">
          <h3>📊 結果管理功能</h3>
          <ul>
            <li>成績錄入 - 實時錄入比賽成績</li>
            <li>結果查詢 - 按項目、班級、學生查詢結果</li>
            <li>排行榜 - 自動生成各項目排行榜</li>
            <li>記錄管理 - 管理學校記錄和個人最佳</li>
            <li>成績統計 - 班級和年級成績統計</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderAnnouncements = () => (
    <div className="sports-announcements">
      <div className="view-header">
        <button className="btn btn-back" onClick={() => handleViewChange('dashboard')}>
          ← 返回儀表板
        </button>
        <h2>公告管理</h2>
        <button className="btn btn-primary">+ 新增公告</button>
      </div>
      
      <div className="announcements-content">
        <div className="announcements-list">
          {sportsAnnouncements.map(announcement => (
            <div key={announcement.id} className="announcement-card">
              <div className="announcement-header">
                <h4>{announcement.title}</h4>
                <div className="announcement-meta">
                  <span className={`priority-badge ${announcement.priority}`}>
                    {announcement.priority === 'urgent' ? '緊急' : 
                     announcement.priority === 'high' ? '高' : 
                     announcement.priority === 'normal' ? '一般' : '低'}
                  </span>
                  <span className="announcement-time">
                    {new Date(announcement.published_at).toLocaleString('zh-TW')}
                  </span>
                </div>
              </div>
              <div className="announcement-body">
                <p>{announcement.content}</p>
                <div className="announcement-tags">
                  <span className="announcement-type">
                    {announcement.announcement_type === 'race_call' ? '🏃‍♀️ 比賽召集' :
                     announcement.announcement_type === 'schedule_update' ? '📅 賽程更新' :
                     announcement.announcement_type === 'result_announcement' ? '🏆 結果公布' :
                     announcement.announcement_type === 'emergency_alert' ? '⚠️ 緊急通知' : '📢 一般公告'}
                  </span>
                  {announcement.target_audience && announcement.target_audience.length > 0 && (
                    <span className="target-audience">
                      目標: {announcement.target_audience.join(', ')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="announcements-actions">
          <h3>📢 公告功能</h3>
          <ul>
            <li>比賽召集通知 - 實時召集參賽選手</li>
            <li>賽程更新 - 天氣、設備問題導致的賽程變更</li>
            <li>結果公布 - 比賽結果和獲獎名單</li>
            <li>緊急通知 - 天氣警告、安全問題、取消通知</li>
            <li>訓練提醒 - 賽前訓練安排和注意事項</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'events':
        return renderEvents();
      case 'registration':
        return renderRegistration();
      case 'results':
        return renderResults();
      case 'announcements':
        return renderAnnouncements();
      default:
        return renderDashboard();
    }
  };

  if (loading) {
    return (
      <div className="sports-loading">
        <div className="loading-spinner"></div>
        <p>載入體育日資料中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sports-error">
        <div className="error-icon">❌</div>
        <h3>載入錯誤</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchSportsData}>
          重新載入
        </button>
      </div>
    );
  }

  return (
    <div className="sports-days-module">
      {/* Navigation Tabs */}
      <div className="sports-navigation">
        <button 
          className={`nav-tab ${activeView === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleViewChange('dashboard')}
        >
          🏠 儀表板
        </button>
        <button 
          className={`nav-tab ${activeView === 'events' ? 'active' : ''}`}
          onClick={() => handleViewChange('events')}
        >
          📅 活動管理
        </button>
        <button 
          className={`nav-tab ${activeView === 'registration' ? 'active' : ''}`}
          onClick={() => handleViewChange('registration')}
        >
          📝 報名管理
        </button>
        <button 
          className={`nav-tab ${activeView === 'results' ? 'active' : ''}`}
          onClick={() => handleViewChange('results')}
        >
          🏆 比賽結果
        </button>
        <button 
          className={`nav-tab ${activeView === 'announcements' ? 'active' : ''}`}
          onClick={() => handleViewChange('announcements')}
        >
          📢 公告管理
        </button>
      </div>

      {/* Main Content */}
      <div className="sports-content">
        {renderContent()}
      </div>

      {/* Back Button */}
      <div className="module-footer">
        <button className="btn btn-back" onClick={onBack}>
          ← 返回主頁
        </button>
      </div>
    </div>
  );
};

export default SportsDaysModule;